<template>
	<GridLayout
		right-title="CONVÊNIO"
		:reactive-sidebar="false"
		:sidebar-state="false"
		:back-action="() => $router.push({ name: 'Partnerships' })"
	>
		<template #right-content>
			<MainContent class="px-4 py-4">
				<form
					class="flex flex-wrap"
					@submit.prevent="Save"
				>
					<div class="data-group">
						<FormGroup
							v-model.trim="form.nome"
							label="NOME"
							class="w-full"
							:required="true"
							:input-classes="[{
								'field-error': $v.form.nome.$error,
							}]"
							:omit-info="true"
						/>
					</div>
					<div class="data-group">
						<FormGroup
							v-model="form.razao_social"
							class="w-full md:w-8/12"
							label="RAZÃO SOCIAL"
							placeholder="RAZÃO SOCIAL"
							:omit-info="true"
						/>

						<FormGroup
							v-model="form.cnpj"
							class="w-full md:w-8/12"
							label="CNPJ"
							formatter="cnpj"
							placeholder="00.000.000/0000-00"
							:input-classes="[{
								'field-error': $v.form.cnpj.$error,
							}]"
							:omit-info="true"
						/>
					</div>
					<div class="data-group">
						<FormGroup
							v-model="form.cep"
							class="w-full md:w-3/12"
							label="CEP"
							formatter="cep"
							placeholder="00000-000"
							:omit-info="true"
						/>

						<FormGroup
							v-model="form.logradouro"
							class="w-full md:w-6/12 lg:w-7/12"
							label="LOGRADOURO"
							placeholder="LOGRADOURO"
							:omit-info="true"
						/>

						<FormGroup
							v-model="form.numero"
							class="w-full md:w-3/12 lg:w-2/12"
							label="NÚMERO"
							formatter="numbers"
							placeholder="NÚMERO"
							:omit-info="true"
						/>
					</div>
					<div class="data-group">
						<FormGroup
							v-model="form.complemento"
							class="w-full md:w-6/12"
							label="COMPLEMENTO"
							placeholder="COMPLEMENTO"
							:omit-info="true"
						/>

						<FormGroup
							v-model="form.bairro"
							class="w-full md:w-6/12"
							label="BAIRRO"
							placeholder="BAIRRO"
							:omit-info="true"
						/>
					</div>
					<div class="data-group">
						<FormGroup
							v-model="form.municipio"
							class="w-full md:w-8/12"
							label="MUNICÍPIO"
							placeholder="MUNICÍPIO"
							:omit-info="true"
						/>

						<FormGroup
							v-model="form.estado"
							class="w-full md:w-4/12"
							input-type="select"
							label="ESTADO"
							placeholder="ESTADO"
							:options="STATES"
							:omit-info="true"
						/>
					</div>
					<div class="data-group">
						<FormGroup
							v-model="form.telefone"
							class="w-full md:w-6/12"
							label="TELEFONE"
							formatter="phone"
							placeholder="(00) 00000-0000"
							:omit-info="true"
						/>

						<FormGroup
							v-model="form.ans_registration"
							class="w-full md:w-6/12"
							label="REGISTRO ANS"
							formatter="numbers"
							:max-length="6"
							placeholder="Nº DO REGISTRO ANS"
							:required="form.billing_type === 'tiss'"
							:input-classes="[{ 'field-error': $v.form.ans_registration.$error }]"
						>
							<template #tooltip>
								<ul class="info-modal-list">
									<li
										v-for="(info, idx) in GetInfoList(
											'ANS'
										)"
										:key="idx"
										class="info-modal-item"
									>
										{{ info }}
									</li>
								</ul>
							</template>
						</FormGroup>
					</div>
					
					<div class="data-group">
						<FormGroup
							v-model="form.executing_provider_code"
							class="w-full md:w-6/12"
							label="CÓDIGO DO CONTRATADO EXECUTANTE NA OPERADORA"
							formatter="numbers"
							:max-length="14"
							placeholder="CÓDIGO DO CONTRATADO EXECUTANTE NA OPERADORA"
							:required="form.billing_type === 'tiss'"
							:input-classes="[{ 'field-error': $v.form.executing_provider_code.$error }]"
						>
							<template #tooltip>
								<ul class="info-modal-list">
									<li
										v-for="(info, idx) in GetInfoList(
											'EXECUTING_PROVIDER_CODE'
										)"
										:key="idx"
										class="info-modal-item"
									>
										{{ info }}
									</li>
								</ul>
							</template>
						</FormGroup>

						<FormGroup
							v-model="form.ativo"
							entity-key="status"
							checkbox-label="ATIVO"
							input-type="checkbox"
							class="w-full md:w-6/12"
						>
							<template #tooltip>
								<ul class="info-modal-list">
									<li class="info-modal-item">
										DEFINIR SE ESTE CONVÊNIO ESTÁ ATIVO
									</li>
								</ul>
							</template>
						</FormGroup>
					</div>
					
					<FormGroup
						v-model="form.billing_type"
						input-type="radio"
						class="w-full"
						label="TIPO DE FATURAMENTO"
						:options="billingTypeOptions"
						:omit-info="true"
					/>
					<div
						v-if="form.billing_type === 'default'"
						class="w-full"
					>
						<div class="form-group">
							<div class="default-fields-container">
								<HierarchySelect
									:value="defaultFieldsHierarchy"
									key-name="default-group"
									identifier="default-group"
									:with-select-all="false"
									@change="OnDefaultFieldsChange"
								/>
							</div>
						</div>
					</div>
					<div
						v-if="form.billing_type === 'tiss'"
						class="w-full"
					>
						<div class="form-group">
							<div class="tiss-fields-container">
								<HierarchySelect
									:value="tissFieldsHierarchy"
									key-name="tiss-group"
									identifier="tiss-group"
									:with-select-all="false"
									@change="OnTissFieldsChange"
								/>
							</div>
						</div>
					</div>

					<FormGroup
						v-if="form.billing_type === 'tiss'"
						v-model="form.tiss_version"
						input-type="select"
						class="w-full"
						label="Versão do TISS"
						placeholder="SELECIONE"
						:options="tissVersions"
						:omit-info="true"
					/>

					<div
						v-if="form.billing_type === 'tiss'"
						class="w-full"
					>
						<div class="form-group">
							<div class="tuss-fields-container">
								<HierarchySelect
									:value="tussHierarchy"
									key-name="tuss-table"
									identifier="tuss-table"
									:with-select-all="false"
									selection-mode="radio"
									:required="true"
									radio-group-name="tuss-version"
									@select="OnTussVersionChange"
								/>
							</div>
						</div>
					</div>

					<FormGroup
						v-model="form.observacao"
						input-type="textarea"
						class="w-full"
						label="OBSERVAÇÕES"
						placeholder="Digite suas observações"
						:omit-info="true"
					/>
				</form>
			</MainContent>
		</template>
		<template #right-footer>
			<div class="w-full h-full flex justify-end">
				<button
					type="button"
					class="btn btn-success btn-fixed-size"
					@click="Save"
				>
					SALVAR
				</button>
			</div>
		</template>
	</GridLayout>
</template>

<script>
	import Helpers from '../../helpers'
	import HierarchySelect from '@/components/shared/HierarchySelect'
	const helpers = new Helpers()
	const _ = require('lodash')

	import {
		required,
		minLength,
		sameAs,
		requiredIf,
	} from 'vuelidate/lib/validators'

	import { PARTNERSHIP_INFOS } from '@/constants/PartnershipInfos'

	const ValidateDocument = value => {
		if (!value) {
			return true
		}

		return helpers.ValidaCNPJ(value)
	}

	const ValidateAnsLength = value => {
		const digits = (value || '').replace(/\D/g, '')
		return digits.length === 0 || digits.length === 6
	}

	const ValidateExecutingProviderCodeLength = value => {
		const digits = (value || '').replace(/\D/g, '')
		return digits.length === 0 || digits.length >= 14
	}

	const _DEFAULT_FIELDS_HIERARCHY = [
		{
			id: 'indicator',
			name: 'SOLICITANTE',
			identifier: 'default-field',
			key: 'default-field',
			selected: false,
			expanded: false,
		}
	]

	const _TISS_FIELDS_HIERARCHY = [
		{
			id: 1,
			name: 'DADOS DO PACIENTE',
			identifier: 'tiss-field',
			key: 'tiss-field',
			selected: false,
			expanded: false,
			children: [
				{
					id: 'wallet_number',
					name: 'Nº CARTEIRA',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
				{
					id: 'wallet_validity',
					name: 'VALIDADE CARTEIRA',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
				{
					id: 'plan_registration',
					name: 'MATRÍCULA DO PLANO',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				}
			],
		},
		{
			id: 'indicator',
			name: 'SOLICITANTE',
			identifier: 'tiss-field',
			key: 'tiss-field',
			selected: false,
			expanded: false,
		},
		{
			id: 3,
			name: 'DADOS DA AUTORIZAÇÃO',
			identifier: 'tiss-field',
			key: 'tiss-field',
			selected: false,
			expanded: false,
			children: [
				{
					id: 'guide_number',
					name: 'Nº GUIA OPERADORA',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
				{
					id: 'authorization',
					name: 'AUTORIZAÇÃO',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
				{
					id: 'authorization_validity',
					name: 'VALIDADE DA AUTORIZAÇÃO',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
				{
					id: 'authorization_date',
					name: 'DATA DA AUTORIZAÇÃO',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
				{
					id: 'validation_code',
					name: 'CÓDIGO DE VALIDAÇÃO',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
				{
					id: 'provider_guide_number',
					name: 'Nº GUIA NO PRESTADOR',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
				{
					id: 'main_guide_number',
					name: 'Nº GUIA PRINCIPAL',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
			],
		},
		{
			id: 4,
			name: 'DETALHES DO ATENDIMENTO',
			identifier: 'tiss-field',
			key: 'tiss-field',
			selected: false,
			expanded: false,
			children: [
				{
					id: 'request_date',
					name: 'DATA DA SOLICITAÇÃO',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
				{
					id: 'attendance_type',
					name: 'TIPO DE ATENDIMENTO',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
				{
					id: 'attendance_character',
					name: 'CARÁTER ATENDIMENTO',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
				{
					id: 'regime',
					name: 'REGIME',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
				{
					id: 'consultation_type',
					name: 'TIPO DE CONSULTA',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
				{
					id: 'special_coverage',
					name: 'COBERTURA ESPECIAL',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
				{
					id: 'accident_indication',
					name: 'INDICAÇÃO DE ACIDENTE',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
				{
					id: 'cid',
					name: 'INDICAÇÃO CLÍNICA (CID-10)',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
				{
					id: 'observations',
					name: 'OBSERVAÇÕES',
					identifier: 'tiss-field',
					key: '',
					selected: false,
					expanded: false,
				},
			],
		},
	]

	import Swal from 'sweetalert2'
	import { mapGetters, mapActions } from 'vuex'
	import { useConfirmation, useDeleteAlert, useError } from '@/composables'
	export default {
		name: 'Form',
		props: {
			partnership_id: {
				type: [String, Number],
				default: null,
			},
		},
		components: {
			HierarchySelect,
		},
		data() {
			const tissVersions = [
				{ label: '4.03.0', value: '4.03.0' },
			]

			const defaultTissVersion = tissVersions[0]?.value || ''

			const billingTypeOptions = [
				{ label: 'PADRÃO', value: 'default' },
				{ label: 'TISS', value: 'tiss' },
			]

			const tuss18Versions = [
				{ label: 'TABELA TUSS 18 VERSÃO 202601', value: '202601' },
				{ label: 'TABELA TUSS 18 VERSÃO 202607', value: '202607' },
			]

			const tuss19Versions = [
				{ label: 'TABELA TUSS 19 VERSÃO 202601', value: '202601' },
				{ label: 'TABELA TUSS 19 VERSÃO 202607', value: '202607' },
			]

			const tuss20Versions = [
				{ label: 'TABELA TUSS 20 VERSÃO 202601', value: '202601' },
				{ label: 'TABELA TUSS 20 VERSÃO 202607', value: '202607' },
			]

			const tuss22Versions = [
				{ label: 'TABELA TUSS 22 VERSÃO 202601', value: '202601' },
				{ label: 'TABELA TUSS 22 VERSÃO 202607', value: '202607' },
			]

			const tuss60Versions = [
				{ label: 'TABELA TUSS 60 VERSÃO 202601', value: '202601' },
				{ label: 'TABELA TUSS 60 VERSÃO 202607', value: '202607' },
			]

			return {
				form: {
					nome: '',
					razao_social: '',
					cnpj: '',
					logradouro: '',
					numero: '',
					bairro: '',
					complemento: '',
					municipio: '',
					estado: '',
					cep: '',
					telefone: '',
					ans_registration: '',
					executing_provider_code: '',
					ativo: true,
					observacao: '',
					required_fields: [],
					billing_type: 'default', // default, tiss ou sigtap
					tiss_version: defaultTissVersion,
					tuss_18_version: '',
					tuss_19_version: '',
					tuss_20_version: '',
					tuss_22_version: '',
					tuss_60_version: '',
				},
				tissVersions,
				billingTypeOptions,
				defaultFieldsHierarchy: _.cloneDeep(_DEFAULT_FIELDS_HIERARCHY),
				tissFieldsHierarchy: _.cloneDeep(_TISS_FIELDS_HIERARCHY),
				tuss18Versions,
				tuss19Versions,
				tuss20Versions,
				tuss22Versions,
				tuss60Versions,
				tussHierarchy: this.CreateTussHierarchy([
					{ table: 22, label: 'PROCEDIMENTOS', versions: tuss22Versions },
					{ table: 20, label: 'MEDICAMENTOS', versions: tuss20Versions },
					{ table: 19, label: 'MATERIAIS E OPME', versions: tuss19Versions },
					{ table: 18, label: 'GASES MEDICINAIS', versions: tuss18Versions },
					{ table: 60, label: 'UNIDADES DE MEDIDA', versions: tuss60Versions },
				]),
				initialTussVersions: null,
				infos: {},
				infoModal: {
					show: false,
					type: null,
					direction: 'down',
				},
			}
		},
		validations: {
			form: {
				nome: {
					required,
				},
				cnpj: {
					ValidateDocument,
				},
				ans_registration: {
					required: requiredIf(function () {
						return this.form.billing_type === 'tiss'
					}),
					minLength: ValidateAnsLength,
				},
				executing_provider_code: {
					required: requiredIf(function () {
						return this.form.billing_type === 'tiss'
					}),
					minLength: ValidateExecutingProviderCodeLength,
				},
				tuss_18_version: {
					required: requiredIf(function () {
						return this.form.billing_type === 'tiss'
					}),
				},
				tuss_19_version: {
					required: requiredIf(function () {
						return this.form.billing_type === 'tiss'
					}),
				},
				tuss_20_version: {
					required: requiredIf(function () {
						return this.form.billing_type === 'tiss'
					}),
				},
				tuss_22_version: {
					required: requiredIf(function () {
						return this.form.billing_type === 'tiss'
					}),
				},
				tuss_60_version: {
					required: requiredIf(function () {
						return this.form.billing_type === 'tiss'
					}),
				},
			},
		},
		computed: {
			...mapGetters('auth', ['requester_selected_id']),
			STATES() {
				return window.statesBR?.map(state => ({
					value: state.initials,
					label: state.initials,
				}))
			},
		},
		methods: {
			...mapActions('system', ['StartLoading', 'FinishLoading']),
			CreateTussHierarchy(tables) {
				return tables.map(({ table, label, versions }) => ({
					id: `tuss-${table}`,
					name: label,
					identifier: 'tuss-table',
					key: 'tuss-table',
					expanded: false,
					children: versions.map(version => ({
						id: `tuss-${table}-${version.value}`,
						name: version.label,
						value: version.value,
						field: `tuss_${table}_version`,
						radioGroupName: `tuss-${table}-version`,
						identifier: 'tuss-version',
						key: '',
						selected: false,
						expanded: false,
					})),
				}))
			},
			OnTussVersionChange(selectedVersion) {
				const table = this.tussHierarchy.find(group =>
					group.children.includes(selectedVersion)
				)

				table.children.forEach(version => {
					version.selected = version === selectedVersion
				})
				this.form[selectedVersion.field] = selectedVersion.value
			},
			RestoreTussVersionSelection() {
				this.tussHierarchy.forEach(table => {
					table.children.forEach(version => {
						version.selected = this.form[version.field] === version.value
					})
				})
			},
			GetTussVersions() {
				return ['18', '19', '20', '22', '60'].reduce((versions, table) => {
					versions[`tuss_${table}_version`] = this.form[`tuss_${table}_version`]
					return versions
				}, {})
			},
			HasTussVersionChanges() {
				return !_.isEqual(this.GetTussVersions(), this.initialTussVersions)
			},
			GetPartnership() {
				const vm = this

				vm.StartLoading()

				return window.api
					.call('post', '/get-partnership', {
						account_id: vm.requester_selected_id,
						partnership_id: vm.partnership_id,
					})
					.then(({ data }) => {
						if (data.response) {
							vm.form.nome = data.partnership.nome
							vm.form.razao_social = data.partnership.razao_social
							vm.form.cnpj = data.partnership.cnpj
							vm.form.logradouro = data.partnership.logradouro
							vm.form.numero = data.partnership.numero
							vm.form.bairro = data.partnership.bairro
							vm.form.complemento = data.partnership.complemento
							vm.form.municipio = data.partnership.municipio
							vm.form.estado = data.partnership.estado
							vm.form.cep = data.partnership.cep
							vm.form.telefone = data.partnership.telefone
							vm.form.ans_registration = data.partnership.ans_registration
							vm.form.executing_provider_code = data.partnership.executing_provider_code
							vm.form.ativo = data.partnership.ativo
							vm.form.observacao = data.partnership.observacao
							vm.form.billing_type = data.partnership.billing_type || 'default'
							vm.form.tiss_version = data.partnership.tiss_version || vm.tissVersions[0]?.value || ''

							;['18', '19', '20', '22', '60'].forEach(table => {
								const field = `tuss_${table}_version`
								vm.form[field] = data.partnership[field] || ''
							})
							vm.RestoreTussVersionSelection()
							vm.initialTussVersions = vm.GetTussVersions()
							
							if (data.partnership.required_fields) {
								if (vm.form.billing_type === 'tiss') {
									vm.RestoreTissFieldsSelection(data.partnership.required_fields)
								} else if (vm.form.billing_type === 'default') {
									vm.RestoredefaultFieldsHierarchy(data.partnership.required_fields)
								}
							}
						} else {
							Swal.fire({
								icon: 'error',
								title: 'Opss...',
								text: 'Convênio não encontrado, contate o suporte.',
							}).then(() => {
								vm.$router.back()
							})
						}
					})
					.finally(() => {
						vm.FinishLoading()
					})
			},
			async Save(e) {
				if (e) e.preventDefault()

				this.$v.form.$touch()

				if (!this.$v.form.$invalid) {
					if (this.partnership_id && this.HasTussVersionChanges()) {
						const { value: isConfirmed } = await Swal.fire({
							icon: 'warning',
							title: 'A AÇÃO APAGARÁ TODAS AS CONFIGURAÇÕES SALVAS DA VERSÃO ATUAL SELECIONADA DA TABELA',
							text: 'DESEJA ALTERAR A SELEÇÃO DA VERSÃO DA TABELA?',
							input: 'checkbox',
							inputValue: 0,
							inputValidator: value =>
								!value && 'PARA CONFIRMAR A ALTERAÇÃO É NECESSÁRIO MARCAR O CAMPO.',
							showDenyButton: true,
							reverseButtons: true,
							confirmButtonText: 'SIM',
							denyButtonText: 'NÃO',
							didOpen: () => {
								Swal.getConfirmButton().disabled = true
								Swal.getInput().addEventListener('change', event => {
									Swal.getConfirmButton().disabled = !event.target.checked
								})
							},
							customClass: {
								container: 'themed',
							},
						})

						if (!isConfirmed) return
					}

					this.StartLoading()

					let obj = {
						...this.form,
						account_id: this.requester_selected_id,
					}

					if (this.partnership_id) {
						obj = {
							...obj,
							partnership_id: this.partnership_id,
						}
					}

					window.api
						.call('post', '/save-partnership', obj)
						.then(({ data }) => {
							if (data.response) {
								Swal.fire({
									toast: true,
									position: 'top',
									timer: 3000,
									title: 'Convênio salvo com sucesso.',
									icon: 'success',
								})

								this.$router.push({ name: 'Partnerships' })
							} else {
								Swal.fire({
									title: 'Eita !',
									text: 'Houve um erro ao salvar, contate o suporte.',
									icon: 'error',
								})
							}
						})
						.finally(() => {
							this.FinishLoading()
						})
				} else {
					this.$nextTick(() => {
						const firstError = this.$el.querySelector(
							'.field-error, .vs--error, .date-wrapper--error'
						)

						if (firstError) {
							firstError.scrollIntoView({
								behavior: 'smooth',
								block: 'center',
							})
						}
					})
				}
			},
			async SearchZipCode() {
				const vm = this
				const zipcode = vm.form.cep

				if (zipcode.length == 9) {
					vm.StartLoading()

					helpers
						.searchZipCode(zipcode)
						.then(res => {
							vm.form.logradouro = res.address
							vm.form.bairro = res.district
							vm.form.complemento = res.complement
							vm.form.municipio = res.city
							vm.form.estado = res.state
						})
						.finally(() => {
							vm.FinishLoading()
						})
				}
			},
			OnDefaultFieldsChange() {
				const selected = {}
				this.defaultFieldsHierarchy.forEach(group => {
					if (group.children?.length) {
						group.children.forEach(field => {
							if (field.selected) selected[field.id] = true
						})
					} else if (group.selected) {
						selected[group.id] = true
					}
				})
				this.form.required_fields = selected
			},
			RestoredefaultFieldsHierarchy(selectedFields) {
				this.defaultFieldsHierarchy.forEach(group => {
					if (group.children?.length) {
						group.children.forEach(field => {
							field.selected = !!selectedFields[field.id]
						})
						group.selected =
							group.children.some(c => c.selected) ?? false
					} else {
						group.selected = !!selectedFields[group.id]
					}
				})
				this.OnDefaultFieldsChange()
			},
			OnTissFieldsChange() {
				const selected = {}
				this.tissFieldsHierarchy.forEach(group => {
					if (group.children?.length) {
						group.children.forEach(field => {
							if (field.selected) selected[field.id] = true
						})
					} else if (group.selected) {
						selected[group.id] = true
					}
				})
				this.form.required_fields = selected
			},
			RestoreTissFieldsSelection(selectedFields) {
				this.tissFieldsHierarchy.forEach(group => {
					if (group.children?.length) {
						group.children.forEach(field => {
							field.selected = !!selectedFields[field.id]
						})
						group.selected =
							group.children.some(c => c.selected) ?? false
					} else {
						group.selected = !!selectedFields[group.id]
					}
				})
				this.OnTissFieldsChange()
			},
			OpenInfoModal(type, event) {
				if (this.infoModal.show && this.infoModal.type === type) {
					this.CloseInfoModal()
					return
				}
				document.removeEventListener('click', this.OnDocumentClick)
				const btn = event?.currentTarget
				const rect = btn ? btn.getBoundingClientRect() : null
				const spaceBelow = rect ? window.innerHeight - rect.bottom : 999
				this.infoModal.direction = spaceBelow < 520 ? 'up' : 'down'
				this.infoModal.type = type
				this.infoModal.show = true
				setTimeout(() => {
					document.addEventListener('click', this.OnDocumentClick)
				}, 0)
			},
			OnDocumentClick() {
				this.CloseInfoModal()
			},
			CloseInfoModal() {
				document.removeEventListener('click', this.OnDocumentClick)
				this.infoModal.show = false
				this.infoModal.type = null
			},
			GetInfoList(type) {
				const infoType = type || this.infoModal.type
				if (!infoType) return []
				return PARTNERSHIP_INFOS[infoType] || []
			},
		},
		watch: {
			'form.billing_type'(value) {
				if (!this.partnership_id) {
					if (value === 'tiss') {
						this.OnTissFieldsChange()
					} else if (value === 'default') {
						this.OnDefaultFieldsChange()
					}
				}

				if (value === 'tiss') {
					if (!this.form.tiss_version && this.tissVersions.length) {
						this.form.tiss_version = this.tissVersions[0].value
					}
					return
				}

				if (!this.form.tiss_version && this.tissVersions.length) {
					this.form.tiss_version = this.tissVersions[0].value
				}
			},
		},
		beforeMount() {
			if (this.partnership_id) {
				this.GetPartnership()
			} else {
				this.RestoreTissFieldsSelection({
					wallet_number: true,
					indicator: true,
					provider_guide_number: true,
				})
				this.OnDefaultFieldsChange()
				;['18', '19', '20', '22', '60'].forEach(table => {
					this.form[`tuss_${table}_version`] = '202607'
				})
				this.RestoreTussVersionSelection()
			}
		},
	}
</script>

<style lang="scss" scoped>
.form-check input:checked ~ label:after{
    background: var(--newPrimaryBackgroundColor) !important;
}

.field-label {
	display: flex;
	align-items: center;
	// text-transform: uppercase;
	// font-size: 0.875rem;
	// font-weight: 700;
	// color: #64748b;
	// margin-bottom: 6px;
	// white-space: nowrap;
	padding: 0;
}

.info-button {
	padding: 0;
	border: none;
	background-color: transparent;
	cursor: pointer;

	i {
		font-size: 1.25rem;
		margin-top: 0.3rem;
		color: var(--fontInfoActive);
	}

	&:hover {
		i {
			color: var(--fontColorHover);
		}
	}

	&:disabled {
		cursor: default;

		i {
			color: var(--fontInfoInactive);
		}
	}
}

.info-anchor {
	position: relative;
	display: inline-flex;
	align-items: center;
}

.info-inline-popup {
	position: absolute;
	z-index: 200;
	top: calc(100% + 4px);
	left: 0;
	background-color: var(--tippyBackgroundColor);
	border: 1px solid var(--tippyBorderColor);
	border-radius: 0.5rem;
	padding: 0.75rem;
	width: 380px;
	max-width: calc(100vw - 16px);
	box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
		0 10px 10px -5px rgba(0, 0, 0, 0.04);
	white-space: normal;

	&.info-inline-popup--up {
		top: auto;
		bottom: calc(100% + 4px);
	}
}

.info-modal-list {
	margin: 0;
	padding: 0;
	list-style: none;
}

.info-modal-item {
	padding-top: 0.25rem;
	padding-bottom: 0.25rem;
	color: var(--fontColor);
}
</style>
